import type {
  AuthUser,
  DailyStatPoint,
  InviteSiteUserResult,
  OverviewResponse,
  Site,
  SessionJourneyResponse,
  SessionListItem,
  SiteRole,
  SiteSummary,
  SiteUserRow,
  TopCampaign,
  TopPage,
  TopReferrer,
} from '../types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';
const TOKEN_KEY = 'dmt_dashboard_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new ApiError(401, 'Session expired');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, body.message ?? 'Request failed');
  }

  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ token: string; user: AuthUser; sites: SiteSummary[] | null }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<{ user: AuthUser; sites: SiteSummary[] | null }>('/auth/me'),

  listSites: () => request<{ sites: SiteSummary[] }>('/sites'),

  createSite: (input: { name: string; domain: string; timezone?: string }) =>
    request<{ site: Site }>('/sites', { method: 'POST', body: JSON.stringify(input) }),

  getSite: (siteId: string) => request<{ site: Site }>(`/sites/${siteId}`),

  listSiteUsers: (siteId: string) => request<{ users: SiteUserRow[] }>(`/sites/${siteId}/users`),

  inviteSiteUser: (siteId: string, input: { email: string; name: string; role: SiteRole }) =>
    request<InviteSiteUserResult>(`/sites/${siteId}/users`, { method: 'POST', body: JSON.stringify(input) }),

  removeSiteUser: (siteId: string, userId: string) =>
    request<void>(`/sites/${siteId}/users/${userId}`, { method: 'DELETE' }),

  overview: (siteId: string, date?: string) =>
    request<OverviewResponse>(`/dashboard/overview?${new URLSearchParams({ siteId, ...(date ? { date } : {}) })}`),

  topPages: (siteId: string, from: string, to: string, limit = 10) =>
    request<{ pages: TopPage[] }>(
      `/dashboard/top-pages?${new URLSearchParams({ siteId, from, to, limit: String(limit) })}`,
    ),

  topReferrers: (siteId: string, from: string, to: string, limit = 10) =>
    request<{ referrers: TopReferrer[] }>(
      `/dashboard/top-referrers?${new URLSearchParams({ siteId, from, to, limit: String(limit) })}`,
    ),

  topCampaigns: (siteId: string, from: string, to: string, limit = 10) =>
    request<{ campaigns: TopCampaign[] }>(
      `/dashboard/top-campaigns?${new URLSearchParams({ siteId, from, to, limit: String(limit) })}`,
    ),

  listSessions: (siteId: string, from: string, to: string, limit = 50, offset = 0) =>
    request<{ sessions: SessionListItem[] }>(
      `/dashboard/sessions?${new URLSearchParams({ siteId, from, to, limit: String(limit), offset: String(offset) })}`,
    ),

  sessionDetail: (siteId: string, sessionId: number) =>
    request<SessionJourneyResponse>(`/dashboard/sessions/${sessionId}?${new URLSearchParams({ siteId })}`),

  dailyStats: (siteId: string, from: string, to: string) =>
    request<{ days: DailyStatPoint[] }>(`/dashboard/daily-stats?${new URLSearchParams({ siteId, from, to })}`),
};
